import React, { useLayoutEffect, useMemo, useState } from 'react';
import { BookOpen, Compass, History, LayoutGrid, RefreshCw, Share2, User } from 'lucide-react';
import { calcDaluren, DALUREN_BRANCHES, DALUREN_STEMS, DalurenResult, getShiChenByDateTime } from '../lib/daluren-engine';
import XuanYuanTopBar from './XuanYuanTopBar';

interface DalurenPanPageProps {
  onBack?: () => void;
  dockStyle?: 'ios' | 'classic';
  preferArchiveBack?: boolean;
  pendingRecordId?: string | null;
  onPendingRecordConsumed?: (recordId: string) => void;
  onNavigateTab?: (tab: 'pan' | 'practice' | 'study' | 'mine') => void;
}

type LiveTimeMode = 'current' | 'reported' | 'random';
type GenderMode = 'male' | 'female';
type GuiTypeMode = 'auto' | 'day' | 'night';
type GuiRenRuleMode = 'classic' | 'alternate';
type ChangeMethodMode = 'zhongqi' | 'jieqi';
type HazardMode = 'mengzhongji' | 'deep';
type DalurenBranch = (typeof DALUREN_BRANCHES)[number];

interface DalurenRecord {
  id: string;
  createdAt: number;
  result: DalurenResult;
  options: {
    question: string;
    dateTimeValue: string;
    birthYear: number;
    gender: GenderMode;
    liveTimeMode: LiveTimeMode;
    reportNumber: number;
    changeMethod: ChangeMethodMode;
    guiType: GuiTypeMode;
    guiRenRule: GuiRenRuleMode;
    hazardType: HazardMode;
    useTrueSolar: boolean;
  };
}

const RECORD_KEY = 'daluren_pan_records_v1';

const pad2 = (v: number): string => String(v).padStart(2, '0');

const toDateTimeLocalValue = (date: Date): string => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
);

const parseDateTimeLocal = (value: string): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return d;
};

const loadRecords = (): DalurenRecord[] => {
  try {
    const raw = localStorage.getItem(RECORD_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DalurenRecord[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && item.id && item.result && item.options);
  } catch {
    return [];
  }
};

const saveRecords = (records: DalurenRecord[]): void => {
  try {
    localStorage.setItem(RECORD_KEY, JSON.stringify(records.slice(0, 30)));
  } catch {
    // ignore
  }
};

const toBranchByNumber = (n: number): DalurenBranch => DALUREN_BRANCHES[Math.max(0, Math.min(11, n - 1))] as DalurenBranch;

const copyResultText = async (result: DalurenResult): Promise<void> => {
  const lines: string[] = [
    `事项：${result.question || '未填写'}`,
    `课式：${result.keStyle}`,
    `时间：${result.dateText}`,
    `节气：${result.solarTermRange}`,
    `四柱：${result.yearPillar}年 ${result.monthPillar}月 ${result.dayPillar}日 ${result.hourPillar}时`,
    `月将：${result.monthGeneral}，占时：${result.zhanShi}`,
    `课体：${result.guaTi.join('，') || '-'}`,
  ];
  const narrativeRows = buildNarrativeRows(result).filter((row) => row.label !== '课体');
  if (narrativeRows.length > 0) {
    lines.push('');
    narrativeRows.forEach((row) => {
      lines.push(`${row.label}：${row.text}`);
    });
  }
  const text = lines.join('\n');
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const showTianJiang = (value: string): string => (value === '雀' ? '朱' : value);
const BRANCH_SET = new Set(DALUREN_BRANCHES);
const STEM_SET = new Set(DALUREN_STEMS);
const JI_GONG_BY_STEM: Record<string, string> = {
  甲: '寅',
  乙: '辰',
  丙: '巳',
  丁: '未',
  戊: '巳',
  己: '未',
  庚: '申',
  辛: '戌',
  壬: '亥',
  癸: '丑',
};

const RI_JU_BY_XUN_SHOU: Record<string, number> = {
  子: 10,
  戌: 8,
  申: 6,
  午: 4,
  辰: 2,
  寅: 12,
};

const CHINESE_NUM: Record<number, string> = {
  1: '一',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六',
  7: '七',
  8: '八',
  9: '九',
  10: '十',
  11: '十一',
  12: '十二',
};

interface DalurenNarrativeRow {
  label: string;
  text: string;
}

const NARRATIVE_MARKERS = [
  '课义',
  '解曰',
  '断曰',
  '占主',
  '象曰',
  '订论',
  '订讹',
  '指要',
  '毕法云',
  '天时',
  '家宅',
  '仕宦',
  '求财',
  '婚姻',
  '疾病',
  '出行',
  '行人',
  '胎产',
  '出兵',
] as const;

const normalizeNarrativeText = (raw: string): string => String(raw || '')
  .replace(/\r/g, '')
  .replace(/\u3000/g, ' ')
  .replace(/[ \t]+/g, ' ')
  .replace(/\n{2,}/g, '\n')
  .trim();

const splitParagraphs = (raw: string): string[] => normalizeNarrativeText(raw)
  .split('\n')
  .map((line) => line.trim())
  .map((line) => line.replace(/^《[^》]+》[：:]\s*/, '').trim())
  .filter(Boolean);

const pickSentence = (raw: string): string => {
  const text = normalizeNarrativeText(raw);
  if (!text) return '';
  const parts = text.split(/(?<=[。！？；])/u).map((v) => v.trim()).filter(Boolean);
  if (parts.length === 0) return text;
  if (parts.length === 1) return parts[0];
  if (parts[0].length >= 42) return parts[0];
  return `${parts[0]}${parts[1]}`;
};

const buildMarkerMap = (raw: string): Record<string, string[]> => {
  const text = normalizeNarrativeText(raw);
  const markerRegex = new RegExp(`(${NARRATIVE_MARKERS.join('|')})[：:]`, 'gu');
  const matches = Array.from(text.matchAll(markerRegex));
  const out: Record<string, string[]> = {};
  if (matches.length === 0) return out;

  for (let i = 0; i < matches.length; i += 1) {
    const current = matches[i];
    const next = matches[i + 1];
    const marker = current[1];
    const start = (current.index || 0) + current[0].length;
    const end = next?.index ?? text.length;
    const chunk = text.slice(start, end).trim();
    if (!chunk) continue;
    if (!out[marker]) out[marker] = [];
    out[marker].push(chunk);
  }
  return out;
};

const mergeSources = (pan: DalurenResult): string[] => {
  const seen = new Set<string>();
  const list: string[] = [];
  pan.guaComments.forEach((item) => {
    (item.sources || []).forEach((name) => {
      const key = String(name || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      list.push(key);
    });
  });
  return list;
};

const buildNarrativeRows = (pan: DalurenResult): DalurenNarrativeRow[] => {
  const rows: DalurenNarrativeRow[] = [
    { label: '课体', text: pan.guaTi.join('，') || '未识别' },
  ];
  if (pan.guaComments.length === 0) return rows;

  const merged = pan.guaComments.map((item) => item.content).join('\n');
  const markerMap = buildMarkerMap(merged);
  const primaryParagraphs = splitParagraphs(pan.guaComments[0].content)
    .filter((line) => !/^（?.{0,20}注[）)]?/u.test(line));

  const firstByRegex = (list: string[], pattern: RegExp): string => (
    list.find((line) => pattern.test(line)) || ''
  );

  const keYi = markerMap['课义']?.[0]
    || firstByRegex(primaryParagraphs, /统[^。；\n]{0,40}之体/u)
    || pickSentence(primaryParagraphs[0] || '');

  const jieYue = markerMap['解曰']?.[0]
    || markerMap['占主']?.[0]
    || pickSentence(firstByRegex(primaryParagraphs, /占主/u))
    || pickSentence(primaryParagraphs[1] || '');

  const duanYue = markerMap['断曰']?.[0]
    || markerMap['象曰']?.[0]
    || markerMap['订论']?.[0]
    || markerMap['订讹']?.[0]
    || markerMap['指要']?.[0]
    || pickSentence(primaryParagraphs[2] || '');

  if (keYi) rows.push({ label: '课义', text: normalizeNarrativeText(keYi) });
  if (jieYue) rows.push({ label: '解曰', text: normalizeNarrativeText(jieYue) });
  if (duanYue) rows.push({ label: '断曰', text: normalizeNarrativeText(duanYue) });

  const sceneFields = ['天时', '家宅', '仕宦', '求财', '婚姻', '疾病', '出行', '行人', '胎产', '出兵'] as const;
  const sceneLine = sceneFields
    .map((field) => {
      const value = markerMap[field]?.[0];
      return value ? `${field}：${normalizeNarrativeText(value)}` : '';
    })
    .filter(Boolean)
    .join(' ');
  if (sceneLine) rows.push({ label: '占断', text: sceneLine });

  const biFa = markerMap['毕法云']?.[0]
    || pan.guaComments.map((item) => item.content).find((line) => line.includes('毕法云') || line.includes('毕法'))?.replace(/^.*?毕法云?[：:]/u, '');
  if (biFa) rows.push({ label: '毕法云', text: normalizeNarrativeText(biFa) });

  const sources = mergeSources(pan);
  if (sources.length > 0) rows.push({ label: '文献', text: sources.join('、') });

  return rows;
};

const DalurenPanPage: React.FC<DalurenPanPageProps> = ({
  onBack,
  onNavigateTab,
  dockStyle = 'ios',
  preferArchiveBack = false,
  pendingRecordId = null,
  onPendingRecordConsumed,
}) => {
  const [question, setQuestion] = useState('');
  const [dateTimeValue, setDateTimeValue] = useState(() => toDateTimeLocalValue(new Date()));
  const [birthYear, setBirthYear] = useState(1990);
  const [gender, setGender] = useState<GenderMode>('male');
  const [liveTimeMode, setLiveTimeMode] = useState<LiveTimeMode>('current');
  const [reportNumber, setReportNumber] = useState(1);
  const [changeMethod, setChangeMethod] = useState<ChangeMethodMode>('zhongqi');
  const [guiRenRule, setGuiRenRule] = useState<GuiRenRuleMode>('classic');
  const [guiType, setGuiType] = useState<GuiTypeMode>('auto');
  const [hazardType, setHazardType] = useState<HazardMode>('mengzhongji');
  const [useTrueSolar, setUseTrueSolar] = useState(false);
  const [result, setResult] = useState<DalurenResult | null>(null);
  const [showConfig, setShowConfig] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showVertical, setShowVertical] = useState(true);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [records, setRecords] = useState<DalurenRecord[]>(() => loadRecords());

  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const list: number[] = [];
    for (let y = now; y >= 1940; y -= 1) list.push(y);
    return list;
  }, []);

  const resolveZhanShi = (dtValue: string): DalurenBranch => {
    if (liveTimeMode === 'reported') {
      return toBranchByNumber(reportNumber);
    }
    if (liveTimeMode === 'random') {
      const idx = Math.floor(Math.random() * 12);
      return DALUREN_BRANCHES[idx] as DalurenBranch;
    }
    return getShiChenByDateTime(dtValue);
  };

  const persistRecord = (nextResult: DalurenResult): void => {
    if (!question || !String(question).trim()) return;
    const rec: DalurenRecord = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      createdAt: Date.now(),
      result: nextResult,
      options: {
        question,
        dateTimeValue,
        birthYear,
        gender,
        liveTimeMode,
        reportNumber,
        changeMethod,
        guiType,
        guiRenRule,
        hazardType,
        useTrueSolar,
      },
    };
    setRecords((prev) => {
      const next = [rec, ...prev].slice(0, 30);
      saveRecords(next);
      return next;
    });
  };

  const runCalc = (targetDateTime?: string): void => {
    setError('');
    setNotice('');
    const dtValue = targetDateTime || dateTimeValue;
    const parsed = parseDateTimeLocal(dtValue);
    if (!parsed) {
      setError('起课时间格式不正确');
      return;
    }

    try {
      const zhanShi = resolveZhanShi(dtValue);
      const next = calcDaluren({
        question,
        dateTime: dtValue,
        birthYear,
        gender,
        zhanShi,
        guiType,
        changeMethod,
        guiRenRule,
      });
      setDateTimeValue(next.dateTimeValue);
      setResult(next);
      setShowConfig(false);
      persistRecord(next);
      if (useTrueSolar) {
        setNotice('当前版本先按本地时区起课，真太阳时已记录开关，后续可继续扩展。');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '起课失败');
    }
  };

  const runPrevNext = (offsetHours: number): void => {
    const base = parseDateTimeLocal(result?.dateTimeValue || dateTimeValue);
    if (!base) return;
    const nextDate = new Date(base.getTime() + offsetHours * 3600 * 1000);
    const nextValue = toDateTimeLocalValue(nextDate);
    setDateTimeValue(nextValue);
    runCalc(nextValue);
  };

  const restoreFromRecord = (item: DalurenRecord): void => {
    setQuestion(item.options.question || item.result.question || '');
    setDateTimeValue(item.options.dateTimeValue || item.result.dateTimeValue);
    setBirthYear(item.options.birthYear || item.result.birthYear || 1990);
    setGender(item.options.gender || (item.result.genderLabel === '女' ? 'female' : 'male'));
    setLiveTimeMode(item.options.liveTimeMode || 'current');
    setReportNumber(item.options.reportNumber || 1);
    setChangeMethod(item.options.changeMethod || 'zhongqi');
    setGuiType(item.options.guiType || 'auto');
    setGuiRenRule(item.options.guiRenRule || 'classic');
    setHazardType(item.options.hazardType || 'mengzhongji');
    setUseTrueSolar(Boolean(item.options.useTrueSolar));
    setResult(item.result);
    setShowConfig(false);
    setShowHistory(false);
    setError('');
  };

  useLayoutEffect(() => {
    const targetId = String(pendingRecordId || '').trim();
    if (!targetId) return;
    const target = records.find((item) => String(item.id) === targetId);
    if (target) {
      restoreFromRecord(target);
    }
    onPendingRecordConsumed?.(targetId);
  }, [pendingRecordId, records, onPendingRecordConsumed]);

  const removeRecord = (id: string): void => {
    setRecords((prev) => {
      const next = prev.filter((item) => item.id !== id);
      saveRecords(next);
      return next;
    });
  };

  const handleShare = async (): Promise<void> => {
    if (!result) return;
    try {
      await copyResultText(result);
      setNotice('已复制排盘内容');
      setError('');
    } catch {
      setError('复制失败，请稍后重试');
    }
  };

  const renderConfigPanel = () => {
    const rowClass = 'flex flex-row items-center gap-2 sm:gap-3 lg:gap-4 px-3 sm:px-4 lg:px-5 py-2.5 sm:py-3';
    const labelClass = 'w-[72px] sm:w-[88px] lg:w-[108px] shrink-0 text-sm sm:text-base lg:text-[17px] font-semibold text-slate-700';
    const ctrlClass = 'min-w-0 w-full sm:flex-1 flex items-center justify-start gap-1.5 sm:gap-2.5 lg:gap-3 text-sm sm:text-base lg:text-[17px] flex-wrap';
    const alignedFieldClass = 'w-full sm:w-[220px] lg:w-[260px] shrink-0';
    const optionClass = 'inline-flex items-center gap-1 sm:gap-1.5 whitespace-nowrap px-0.5 sm:px-1 lg:px-1.5 py-0.5 text-sm sm:text-base lg:text-[17px]';
    const checkClass = 'w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 inline-flex items-center justify-center leading-none font-semibold text-[11px] sm:text-xs';
    const checkOnClass = 'border-[#8b6b3d] bg-[#8b6b3d] text-white';
    const checkOffClass = 'border-[#b5becb] bg-white text-transparent';

    return (
      <section className="bg-white rounded-2xl border border-[#e6dac9] shadow-[0_14px_30px_rgba(98,67,34,0.18)] overflow-hidden w-full max-w-lg sm:max-w-xl md:max-w-2xl mx-auto">
        <div className="p-3 sm:p-4 lg:p-5">
          <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden">
            <div className={rowClass}>
              <div className={labelClass}>事项内容</div>
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="请输入事项(选填)"
                className="min-w-0 flex-1 bg-transparent outline-none text-sm sm:text-base lg:text-[17px] text-slate-700 placeholder:text-slate-400"
              />
            </div>

            <div className={rowClass}>
              <div className={labelClass}>起课时间</div>
              <div className={`${ctrlClass} gap-1.5 flex-nowrap justify-between`}>
                <div className="w-[142px] sm:w-[188px] lg:w-[208px] shrink-0">
                  <input
                    type="datetime-local"
                    value={dateTimeValue}
                    onChange={(e) => setDateTimeValue(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 sm:px-3 sm:py-1.5 lg:py-2 text-xs sm:text-sm lg:text-base"
                  />
                </div>
                <button
                  onClick={() => setDateTimeValue(toDateTimeLocalValue(new Date()))}
                  className="ml-auto inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                  aria-label="设置当前时间"
                >
                  <span>重置</span>
                  <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>

            <div className={rowClass}>
              <div className={labelClass}>出生年份</div>
              <div className={`${ctrlClass} flex-wrap sm:flex-nowrap justify-start gap-2`}>
                <div className="w-[104px] sm:w-[124px] lg:w-[136px] shrink-0">
                  <select
                    value={birthYear}
                    onChange={(e) => setBirthYear(Number(e.target.value))}
                    className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs sm:text-sm"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>{year}年</option>
                    ))}
                  </select>
                </div>
                <div className="shrink-0 flex items-center gap-2 sm:gap-3 text-sm sm:text-base lg:text-[17px] whitespace-nowrap">
                  <button
                    onClick={() => setGender('male')}
                    className={optionClass}
                  >
                    <span className={`${checkClass} ${gender === 'male' ? checkOnClass : checkOffClass}`}>✓</span>
                    男命
                  </button>
                  <button
                    onClick={() => setGender('female')}
                    className={optionClass}
                  >
                    <span className={`${checkClass} ${gender === 'female' ? checkOnClass : checkOffClass}`}>✓</span>
                    女命
                  </button>
                </div>
              </div>
            </div>

            <div className={rowClass}>
              <div className={labelClass}>活时选择</div>
              <div className={ctrlClass}>
                <div className={alignedFieldClass}>
                  <select
                    value={liveTimeMode}
                    onChange={(e) => setLiveTimeMode(e.target.value as LiveTimeMode)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 sm:py-2 text-sm sm:text-base lg:text-[17px]"
                  >
                    <option value="current">当前时间</option>
                    <option value="reported">报数定时</option>
                    <option value="random">随机占时</option>
                  </select>
                </div>
                {liveTimeMode === 'reported' && (
                  <select
                    value={reportNumber}
                    onChange={(e) => setReportNumber(Number(e.target.value))}
                    className="w-full sm:w-[150px] sm:max-w-[150px] rounded-lg border border-slate-300 bg-white px-3 py-1.5 sm:py-2 text-sm sm:text-base lg:text-[17px]"
                  >
                    {Array.from({ length: 12 }).map((_, idx) => (
                      <option key={idx + 1} value={idx + 1}>报数 {idx + 1}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className={rowClass}>
              <div className={labelClass}>换将方式</div>
              <div className={ctrlClass}>
                <button
                  onClick={() => setChangeMethod('jieqi')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${changeMethod === 'jieqi' ? checkOnClass : checkOffClass}`}>✓</span>
                  交节
                </button>
                <button
                  onClick={() => setChangeMethod('zhongqi')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${changeMethod === 'zhongqi' ? checkOnClass : checkOffClass}`}>✓</span>
                  中气
                </button>
              </div>
            </div>

            <div className={rowClass}>
              <div className={labelClass}>贵人求法</div>
              <div className={`${ctrlClass} gap-1.5`}>
                <button
                  onClick={() => setGuiRenRule('classic')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${guiRenRule === 'classic' ? checkOnClass : checkOffClass}`}>✓</span>
                  甲戊庚牛羊
                </button>
                <button
                  onClick={() => setGuiRenRule('alternate')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${guiRenRule === 'alternate' ? checkOnClass : checkOffClass}`}>✓</span>
                  甲羊戊庚牛
                </button>
              </div>
            </div>

            <div className={rowClass}>
              <div className={labelClass}>贵神类型</div>
              <div className={`${ctrlClass} gap-1.5`}>
                <button
                  onClick={() => setGuiType('auto')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${guiType === 'auto' ? checkOnClass : checkOffClass}`}>✓</span>
                  卯酉区分
                </button>
                <button
                  onClick={() => setGuiType('day')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${guiType === 'day' ? checkOnClass : checkOffClass}`}>✓</span>
                  白天
                </button>
                <button
                  onClick={() => setGuiType('night')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${guiType === 'night' ? checkOnClass : checkOffClass}`}>✓</span>
                  夜晚
                </button>
              </div>
            </div>

            <div className={rowClass}>
              <div className={labelClass}>涉害类型</div>
              <div className={ctrlClass}>
                <button
                  onClick={() => setHazardType('mengzhongji')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${hazardType === 'mengzhongji' ? checkOnClass : checkOffClass}`}>✓</span>
                  孟仲季
                </button>
                <button
                  onClick={() => setHazardType('deep')}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${hazardType === 'deep' ? checkOnClass : checkOffClass}`}>✓</span>
                  深浅
                </button>
              </div>
            </div>

            <div className={rowClass}>
              <div className={labelClass}>时间类型</div>
              <div className={ctrlClass}>
                <button
                  onClick={() => setUseTrueSolar((v) => !v)}
                  className={optionClass}
                >
                  <span className={`${checkClass} ${useTrueSolar ? checkOnClass : checkOffClass}`}>✓</span>
                  真太阳时
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 mt-3">
            <button
              onClick={() => runCalc()}
              className="w-full py-2.5 sm:py-3 rounded-xl bg-[#8b6b3d] text-white text-sm sm:text-base lg:text-[17px] font-normal hover:bg-[#785a31]"
            >
              开始起课
            </button>
            <button
              onClick={() => setShowHistory(true)}
              className="w-full py-2.5 sm:py-3 rounded-xl bg-[#8b6b3d] text-white text-sm sm:text-base lg:text-[17px] font-normal hover:bg-[#785a31]"
            >
              起课记录
            </button>
          </div>
        </div>
      </section>
    );
  };

  const getBranchStem = (pan: DalurenResult, branch: string): string => (
    pan.xunDunByBranch[branch as keyof typeof pan.xunDunByBranch] || '○'
  );

  const getHeavenTianJiang = (pan: DalurenResult, heaven: string): string => {
    for (const row of pan.tianPanRows) {
      for (const cell of row) {
        if (cell && cell.heaven === heaven) return showTianJiang(cell.tianJiang);
      }
    }
    return '';
  };

  const stemTextClass = (pan: DalurenResult, stem: string, normal = 'text-slate-400'): string => (
    stem === pan.dayStem ? 'text-red-600' : normal
  );

  const branchTextClass = (_pan: DalurenResult, _branch: string, normal = 'text-slate-900 font-normal'): string => normal;

  const tianJiangTextClass = (tj: string, normal = 'text-slate-500'): string => (
    tj === '贵' ? 'text-red-600' : normal
  );

  const heitiStyle: React.CSSProperties = {
    fontFamily: '"SimHei","Microsoft YaHei","Heiti SC","PingFang SC","Noto Sans CJK SC",sans-serif',
    fontWeight: 400,
  };

  const getSanchuanTag = (pan: DalurenResult, branch: string): string => {
    if (branch === pan.sanchuanRows[0].branch) return '初';
    if (branch === pan.sanchuanRows[1].branch) return '中';
    if (branch === pan.sanchuanRows[2].branch) return '末';
    return '';
  };

  const getRiJuText = (pan: DalurenResult): string => {
    const stem = pan.dayPillar.slice(0, 1);
    const branch = pan.dayPillar.slice(1, 2);
    if (!STEM_SET.has(stem as (typeof DALUREN_STEMS)[number]) || !BRANCH_SET.has(branch as (typeof DALUREN_BRANCHES)[number])) {
      return `${pan.dayPillar}日十局`;
    }

    const stemIdx = DALUREN_STEMS.indexOf(stem as (typeof DALUREN_STEMS)[number]);
    const branchIdx = DALUREN_BRANCHES.indexOf(branch as (typeof DALUREN_BRANCHES)[number]);
    const xunDelta = ((stemIdx % 10) + 10) % 10;
    const xunShou = DALUREN_BRANCHES[((branchIdx - xunDelta) % 12 + 12) % 12];
    const ju = RI_JU_BY_XUN_SHOU[xunShou] || 10;
    return `${pan.dayPillar}日${CHINESE_NUM[ju] || String(ju)}局`;
  };

  const renderStemBranch = (pan: DalurenResult, branch: string) => {
    const stem = getBranchStem(pan, branch);
    return (
      <span className="inline-flex items-baseline gap-0.5">
        <span className={`${stemTextClass(pan, stem, 'text-slate-500')} text-[14px] sm:text-[16px]`}>{stem}</span>
        <span className={`${branchTextClass(pan, branch)} text-[17px] sm:text-[21px]`}>{branch}</span>
      </span>
    );
  };

  const renderBoardCell = (pan: DalurenResult, cell: { heaven: string; tianJiang: string }) => {
    const tag = getSanchuanTag(pan, cell.heaven);
    const tj = showTianJiang(cell.tianJiang);
    const stem = getBranchStem(pan, cell.heaven);
    return (
      <div className="relative h-full w-full leading-tight">
        {tag ? <span className="absolute left-1 top-1 text-slate-400 text-[14px] sm:text-[16px]">{tag}</span> : null}
        <span
          className={`absolute right-1 top-1 text-[14px] sm:text-[16px] ${tianJiangTextClass(tj, 'text-slate-500')}`}
        >
          {tj}
        </span>
        <span
          className={`absolute right-[2rem] sm:right-[2.2rem] bottom-1 text-[14px] sm:text-[16px] ${stemTextClass(pan, stem, 'text-slate-500')}`}
        >
          {stem}
        </span>
        <span
          className={`absolute right-1 bottom-1 text-[17px] sm:text-[21px] leading-none ${branchTextClass(pan, cell.heaven)}`}
        >
          {cell.heaven}
        </span>
      </div>
    );
  };

  const renderHorizontalSikeToken = (pan: DalurenResult, value: string, key: string) => {
    if (BRANCH_SET.has(value as (typeof DALUREN_BRANCHES)[number])) {
      return (
        <span key={key} className="inline-flex items-baseline">
          {renderStemBranch(pan, value)}
        </span>
      );
    }
    const jiGong = JI_GONG_BY_STEM[value] || '';
    return (
      <span key={key} className="inline-flex items-baseline gap-0.5">
        <span className="text-slate-400">{jiGong}</span>
        <span className={value === pan.dayStem ? 'text-red-600' : 'text-slate-900 font-semibold'}>{value}</span>
      </span>
    );
  };

  const getBoardData = (pan: DalurenResult) => {
    const top = pan.tianPanRows[0].filter(Boolean) as Array<{ heaven: string; tianJiang: string }>;
    const leftUpper = pan.tianPanRows[1][0];
    const rightUpper = pan.tianPanRows[1][3];
    const leftLower = pan.tianPanRows[2][0];
    const rightLower = pan.tianPanRows[2][3];
    const bottom = pan.tianPanRows[3].filter(Boolean) as Array<{ heaven: string; tianJiang: string }>;
    if (!leftUpper || !rightUpper || !leftLower || !rightLower || top.length !== 4 || bottom.length !== 4) {
      return null;
    }
    return { top, leftUpper, rightUpper, leftLower, rightLower, bottom };
  };

  const renderClassicVertical = (pan: DalurenResult) => {
    const board = getBoardData(pan);
    if (!board) return null;
    const plateFontClass = 'text-[14px] sm:text-[16px] leading-[1.2]';
    const coreGlyphClass = 'text-[17px] sm:text-[21px] font-normal leading-none';
    const miniGlyphClass = 'text-[14px] sm:text-[16px] text-slate-400 leading-none';
    const tinyGlyphClass = 'text-[11px] sm:text-[13px] text-slate-400 leading-none';
    const sikeCellClass = 'inline-grid min-w-[3.8em] sm:min-w-[4.4em] grid-cols-[0.72em_2em_0.95em] sm:grid-cols-[0.8em_2.35em_1.05em] items-end justify-items-start gap-x-0 whitespace-nowrap';

    return (
      <div className="space-y-6" style={heitiStyle}>
        <div className={`${plateFontClass} text-slate-900 flex flex-col items-center`}>
          {pan.sanchuanRows.map((row, idx) => (
            <div key={`v-sc-${idx}`} className="flex items-baseline gap-1">
              <span className={miniGlyphClass}>{row.relation}</span>
              <span className={`${stemTextClass(pan, row.dunGan || '○', 'text-slate-400')} ${miniGlyphClass}`}>{row.dunGan || '○'}</span>
              <span className={`${branchTextClass(pan, row.branch)} ${coreGlyphClass}`}>{row.branch}</span>
              <span className={`${tianJiangTextClass(showTianJiang(row.tianJiang), 'text-slate-400')} ${miniGlyphClass}`}>{showTianJiang(row.tianJiang)}</span>
              <span className={miniGlyphClass}>{idx === 0 ? '初' : idx === 1 ? '中' : '末'}</span>
            </div>
          ))}
        </div>

        <div className={`${plateFontClass} text-slate-900 flex flex-col items-center`}>
          <div className="grid grid-cols-4 gap-x-0.5 gap-y-0.5 sm:gap-x-1">
            {pan.sikeTianJiangRows[0].map((v, idx) => (
              <span key={`vs-tj-${idx}`} className={sikeCellClass}>
                <span className={`invisible ${miniGlyphClass}`}>〇</span>
                <span className={`invisible ${tinyGlyphClass}`}>（○）</span>
                <span className={`${tianJiangTextClass(showTianJiang(v), 'text-slate-400')} ${miniGlyphClass}`}>
                  {showTianJiang(v)}
                </span>
              </span>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-x-0.5 gap-y-0.5 sm:gap-x-1 mt-0.5">
            {pan.sikeRows[0].map((value, idx) => (
              <span key={`vs-r0-${idx}`} className={sikeCellClass}>
                <span className={`invisible ${miniGlyphClass}`}>〇</span>
                <span className={`text-slate-400 ${tinyGlyphClass}`}>（{getBranchStem(pan, value)}）</span>
                <span className={`${branchTextClass(pan, value)} ${coreGlyphClass}`}>{value}</span>
              </span>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-x-0.5 gap-y-0.5 sm:gap-x-1 mt-0.5">
            {pan.sikeRows[1].map((value, idx) => {
              const raw = String(value);
              if (BRANCH_SET.has(raw as (typeof DALUREN_BRANCHES)[number])) {
                const assist = getHeavenTianJiang(pan, raw) || '○';
                return (
                  <span key={`vs-r1-${idx}`} className={sikeCellClass}>
                    <span className={`invisible ${miniGlyphClass}`}>〇</span>
                    <span className={`text-slate-400 ${tinyGlyphClass}`}>（{assist}）</span>
                    <span className={`${branchTextClass(pan, raw)} ${coreGlyphClass}`}>{raw}</span>
                  </span>
                );
              }
              const ji = JI_GONG_BY_STEM[raw] || '○';
              const shen = ji === '○' ? '' : getHeavenTianJiang(pan, ji);
              return (
                <span key={`vs-r1-${idx}`} className={sikeCellClass}>
                  <span className={`${shen ? tianJiangTextClass(shen, 'text-slate-400') : 'invisible'} ${tinyGlyphClass}`}>{shen || '〇'}</span>
                  <span className={`text-slate-400 ${tinyGlyphClass}`}>（{ji}）</span>
                  <span className={`${stemTextClass(pan, raw, 'text-slate-900')} ${coreGlyphClass}`}>{raw}</span>
                </span>
              );
            })}
          </div>
        </div>

        <div className="mx-auto w-full max-w-[248px] sm:max-w-[304px] lg:max-w-[360px] border-2 border-black bg-white">
          <table className="w-full table-fixed border-collapse">
            <tbody>
              <tr>
                {board.top.map((cell, idx) => (
                  <td key={`vt-${idx}`} className="h-[50px] sm:h-[64px] lg:h-[74px] border border-black">{renderBoardCell(pan, cell)}</td>
                ))}
              </tr>
              <tr>
                <td className="h-[50px] sm:h-[64px] lg:h-[74px] border border-black">{renderBoardCell(pan, board.leftUpper)}</td>
                <td rowSpan={2} colSpan={2} className="border border-black text-center align-middle">
                </td>
                <td className="h-[50px] sm:h-[64px] lg:h-[74px] border border-black">{renderBoardCell(pan, board.rightUpper)}</td>
              </tr>
              <tr>
                <td className="h-[50px] sm:h-[64px] lg:h-[74px] border border-black">{renderBoardCell(pan, board.leftLower)}</td>
                <td className="h-[50px] sm:h-[64px] lg:h-[74px] border border-black">{renderBoardCell(pan, board.rightLower)}</td>
              </tr>
              <tr>
                {board.bottom.map((cell, idx) => (
                  <td key={`vb-${idx}`} className="h-[50px] sm:h-[64px] lg:h-[74px] border border-black">{renderBoardCell(pan, cell)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderClassicHorizontal = (pan: DalurenResult) => {
    const board = getBoardData(pan);
    if (!board) return null;

    const stemClass = (stem: string): string => stemTextClass(pan, stem, 'text-slate-500');
    const branchClass = (branch: string): string => branchTextClass(pan, branch);
    const tjClass = (tj: string): string => tianJiangTextClass(tj, 'text-slate-700');
    const sanchuanTjClass = (tj: string): string => tianJiangTextClass(tj, 'text-slate-400');
    const rowFontClass = 'text-[12px] sm:text-[14px] lg:text-[15px] leading-[1.2]';

    const ringIndentClass = 'pl-[2.2rem] sm:pl-[2.6rem] lg:pl-[2.8rem]';

    const renderHorizontalSikePlain = (value: string, key: string) => {
      if (BRANCH_SET.has(value as (typeof DALUREN_BRANCHES)[number])) {
        return <span key={key} className={branchClass(value)}>{value}</span>;
      }
      return <span key={key} className={value === pan.dayStem ? 'text-red-600 font-semibold' : 'text-slate-900'}>{value}</span>;
    };

    const renderTopRow = (type: 'stem' | 'tj' | 'branch', indented = false) => (
      <div className={`flex items-center gap-2 whitespace-nowrap ${indented ? ringIndentClass : ''}`}>
        {board.top.map((cell, idx) => {
          const stem = getBranchStem(pan, cell.heaven);
          const tj = showTianJiang(cell.tianJiang);
          if (type === 'stem') return <span key={`h-top-stem-${idx}`} className={stemClass(stem)}>{stem}</span>;
          if (type === 'tj') return <span key={`h-top-tj-${idx}`} className={tjClass(tj)}>{tj}</span>;
          return <span key={`h-top-branch-${idx}`} className={branchClass(cell.heaven)}>{cell.heaven}</span>;
        })}
      </div>
    );

    const renderBottomRow = (type: 'stem' | 'tj' | 'branch', indented = false) => (
      <div className={`flex items-center gap-2 whitespace-nowrap ${indented ? ringIndentClass : ''}`}>
        {board.bottom.map((cell, idx) => {
          const stem = getBranchStem(pan, cell.heaven);
          const tj = showTianJiang(cell.tianJiang);
          if (type === 'stem') return <span key={`h-bottom-stem-${idx}`} className={stemClass(stem)}>{stem}</span>;
          if (type === 'tj') return <span key={`h-bottom-tj-${idx}`} className={tjClass(tj)}>{tj}</span>;
          return <span key={`h-bottom-branch-${idx}`} className={branchClass(cell.heaven)}>{cell.heaven}</span>;
        })}
      </div>
    );

    const renderSideRow = (
      left: { heaven: string; tianJiang: string },
      right: { heaven: string; tianJiang: string },
      rowKey: string,
    ) => {
      const leftStem = getBranchStem(pan, left.heaven);
      const rightStem = getBranchStem(pan, right.heaven);
      const leftTj = showTianJiang(left.tianJiang);
      const rightTj = showTianJiang(right.tianJiang);
      return (
        <div className="grid grid-cols-[max-content_52px_max-content] sm:grid-cols-[max-content_64px_max-content] lg:grid-cols-[max-content_72px_max-content] items-center mt-0.5 whitespace-nowrap">
          <div>
            <span className={stemClass(leftStem)}>{leftStem}</span>
            <span className={`ml-1 ${tjClass(leftTj)}`}>{leftTj}</span>
            <span className={`ml-1 ${branchClass(left.heaven)}`}>{left.heaven}</span>
          </div>
          <div />
          <div key={`${rowKey}-right`}>
            <span className={branchClass(right.heaven)}>{right.heaven}</span>
            <span className={`ml-1 ${tjClass(rightTj)}`}>{rightTj}</span>
            <span className={`ml-1 ${stemClass(rightStem)}`}>{rightStem}</span>
          </div>
        </div>
      );
    };

    return (
      <div className="mx-auto max-w-full overflow-x-auto overflow-y-hidden px-1 sm:px-2" style={heitiStyle}>
        <div className="mx-auto w-fit">
          <div className="grid grid-cols-[max-content_max-content] gap-6 sm:gap-8 lg:gap-10 items-center justify-center">
          <div className={`${rowFontClass} text-slate-900`}>
            {renderTopRow('stem', true)}
            <div className="mt-1">{renderTopRow('tj', true)}</div>
            <div className="mt-1">{renderTopRow('branch', true)}</div>
            {renderSideRow(board.leftUpper, board.rightUpper, 'upper')}
            {renderSideRow(board.leftLower, board.rightLower, 'lower')}
            <div className="mt-1">{renderBottomRow('branch', true)}</div>
            <div className="mt-1">{renderBottomRow('tj', true)}</div>
            <div className="mt-1">{renderBottomRow('stem', true)}</div>
          </div>

          <div className={`${rowFontClass} text-slate-900 grid grid-cols-[max-content_max-content] gap-6 sm:gap-8 lg:gap-10 items-start`}>
            <div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                {pan.sikeTianJiangRows[0].map((v, idx) => (
                  <span key={`hs-tj-${idx}`} className={tjClass(showTianJiang(v))}>{showTianJiang(v)}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-1 whitespace-nowrap">
                {pan.sikeRows[0].map((value, idx) => renderHorizontalSikePlain(value, `hs-r0-${idx}`))}
              </div>
              <div className="flex items-center gap-2 mt-1 whitespace-nowrap">
                {pan.sikeRows[1].map((value, idx) => renderHorizontalSikePlain(String(value), `hs-r1-${idx}`))}
              </div>
            </div>

            <div>
              {pan.sanchuanRows.map((row, idx) => (
                <div key={`hs-sc-${idx}`} className="flex items-baseline gap-1.5 mt-1 first:mt-0 whitespace-nowrap">
                  <span>{row.relation}</span>
                  <span className={(row.dunGan || '○') === pan.dayStem ? 'text-red-600' : 'text-slate-900'}>{row.dunGan || '○'}</span>
                  <span className={branchClass(row.branch)}>{row.branch}</span>
                  <span className={sanchuanTjClass(showTianJiang(row.tianJiang))}>{showTianJiang(row.tianJiang)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  };

  const renderResultPanel = (pan: DalurenResult) => (
    <div className="space-y-4">
      <section className="bg-white rounded-2xl border border-slate-200 shadow-none overflow-hidden w-full max-w-3xl mx-auto">
        <table className="w-full table-fixed border-collapse text-sm">
          <tbody>
            <tr>
              <td className="w-[78px] sm:w-[88px] px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">事项</td>
              <td colSpan={4} className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-left">{pan.question || '-'}</td>
            </tr>
            <tr>
              <td className="w-[78px] sm:w-[88px] px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">课式</td>
              <td colSpan={4} className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-left">{pan.keStyle}</td>
            </tr>
            <tr>
              <td className="w-[78px] sm:w-[88px] px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">时间</td>
              <td colSpan={4} className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-left">{pan.dateText}</td>
            </tr>
            <tr>
              <td className="w-[78px] sm:w-[88px] px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">节气</td>
              <td colSpan={4} className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-left">{pan.solarTermRange}</td>
            </tr>
            <tr>
              <td className="w-[78px] sm:w-[88px] px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center align-middle">四柱</td>
              <td className="px-2 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">
                <span className="font-bold text-slate-900">{pan.yearPillar}</span><span className="text-slate-900">年</span>
              </td>
              <td className="px-2 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">
                <span className="font-bold text-slate-900">{pan.monthPillar}</span><span className="text-slate-900">月</span>
              </td>
              <td className="px-2 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">
                <span className="font-bold text-red-600">{pan.dayPillar}</span><span className="text-slate-900">日</span>
              </td>
              <td className="px-2 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">
                <span className="font-bold text-red-600">{pan.hourPillar}</span><span className="text-slate-900">时</span>
              </td>
            </tr>
            <tr>
              <td className="w-[78px] sm:w-[88px] px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">月将</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">性别</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">年命</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">行年</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] font-bold text-[#9c5b31] bg-[#fefbec] border border-slate-200 text-center">空亡</td>
            </tr>
            <tr>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">{pan.monthGeneral}</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">{pan.genderLabel}</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">{pan.benMing}</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">{pan.xingNian}</td>
              <td className="px-2 sm:px-3 py-1 sm:py-2 lg:py-1.5 text-[13px] sm:text-[15px] lg:text-[14px] text-slate-900 border border-slate-200 text-center">{pan.kongWang}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-none p-3 sm:p-4">
        {showVertical ? renderClassicHorizontal(pan) : renderClassicVertical(pan)}

        <div className="grid grid-cols-4 gap-2 mt-4">
          <button
            onClick={() => runPrevNext(-2)}
            className="py-2 rounded-lg bg-[#8b6b3d] text-white text-sm hover:bg-[#785a31]"
          >
            上一时
          </button>
          <button
            onClick={() => runPrevNext(2)}
            className="py-2 rounded-lg bg-[#8b6b3d] text-white text-sm hover:bg-[#785a31]"
          >
            下一时
          </button>
          <button
            onClick={() => setShowVertical((v) => !v)}
            className="py-2 rounded-lg bg-[#8b6b3d] text-white text-sm hover:bg-[#785a31]"
          >
            {showVertical ? '竖版' : '横版'}
          </button>
          <button
            onClick={() => setNotice('神煞模块后续可继续接入。')}
            className="py-2 rounded-lg bg-[#8b6b3d] text-white text-sm hover:bg-[#785a31]"
          >
            神煞
          </button>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-none p-3 sm:p-4">
        <div className="space-y-4">
          {buildNarrativeRows(pan).map((row, idx) => (
            <p key={`${row.label}-${idx}`} className="text-[15px] leading-9 text-slate-900">
              <span className="font-medium">{row.label}：</span>
              {row.text || '—'}
            </p>
          ))}
          {pan.guaComments.length === 0 && (
            <p className="text-sm text-slate-500">当前课体暂无模型注释。</p>
          )}
        </div>
      </section>
    </div>
  );

  const handleHeaderBack = () => {
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    if (result && !showConfig) {
      if (preferArchiveBack) {
        onBack?.();
        return;
      }
      setShowConfig(true);
      setError('');
      setNotice('');
      return;
    }
    onBack?.();
  };

  const isConfigView = showConfig || !result;

  return (
    <div className="min-h-screen bg-[#ebe8e1] text-slate-900">
      <div className="bg-gradient-to-b from-[#a27a45] via-[#b38750] to-[#c79a61] pb-20">
        <XuanYuanTopBar
          title="轩辕国学 · 大六壬起课"
          onBack={handleHeaderBack}
          variant="goldOverlay"
          sticky={false}
          paddingClassName="pt-[calc(env(safe-area-inset-top,0px)+0.85rem)] pb-3.5"
          dataBackMarker="dalurenPan"
          right={result && !showConfig ? (
            <button
              onClick={() => { void handleShare(); }}
              className="inline-flex h-8 items-center gap-1 rounded-full bg-white/90 text-slate-700 px-2.5 text-xs sm:text-sm font-medium hover:bg-white"
            >
              <Share2 className="w-3.5 h-3.5" />
              分享
            </button>
          ) : undefined}
        />
      </div>

      <div className="-mt-16 pb-24">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 space-y-4">
          {(showConfig || !result) && renderConfigPanel()}
          {result && !showConfig && renderResultPanel(result)}

          {(error || notice) && (
            <div className={`rounded-xl p-3 text-sm ${error ? 'border border-rose-200 bg-rose-50 text-rose-700' : 'border border-amber-200 bg-amber-50 text-amber-800'}`}>
              {error || notice}
            </div>
          )}

          {result && !showConfig && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowConfig(true)}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                返回起课设置
              </button>
              <button
                onClick={() => setShowHistory(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                <History className="w-4 h-4" />
                起课记录
              </button>
            </div>
          )}
        </div>
      </div>

      {showHistory && (
        <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">起课记录</h3>
              <button
                onClick={() => setShowHistory(false)}
                className="px-3 py-1 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
              >关闭</button>
            </div>
            <div className="max-h-[62vh] overflow-y-auto">
              {records.length === 0 ? (
                <div className="p-4 text-sm text-slate-500">暂无记录</div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {records.map((item) => (
                    <div key={item.id} className="p-3 flex items-start justify-between gap-3 rounded-xl hover:bg-slate-50 transition-colors">
                      <button
                        onClick={() => restoreFromRecord(item)}
                        className="text-left flex-1"
                      >
                        <p className="text-sm font-medium text-slate-900 line-clamp-1">{item.result.question || '未填写事项'}</p>
                        <p className="text-xs text-slate-500 mt-1">{item.result.dateText} · {item.result.keStyle} · {item.result.guaTi.slice(0, 2).join('，') || '课体未识别'}</p>
                      </button>
                      <button
                        onClick={() => removeRecord(item.id)}
                        className="text-xs text-rose-600 hover:text-rose-700"
                      >
                        删除
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`mobile-tabbar lg:hidden fixed bottom-0 left-0 right-0 z-40 ${dockStyle === 'ios' ? 'pb-safe' : 'mobile-tabbar-default'}`}>
        {dockStyle === 'ios' ? (
          <div className="mobile-tabbar-container">
            <div className="mobile-tabbar-inner">
              <button
                onClick={() => onNavigateTab?.('pan')}
                className="mobile-tabbar-btn text-amber-700"
              >
                <Compass className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">排盘</span>
              </button>
              <button
                onClick={() => onNavigateTab?.('practice')}
                className="mobile-tabbar-btn text-slate-400"
              >
                <LayoutGrid className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">应用</span>
              </button>
              <button
                onClick={() => onNavigateTab?.('study')}
                className="mobile-tabbar-btn text-slate-400"
              >
                <BookOpen className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">学习</span>
              </button>
              <button
                onClick={() => onNavigateTab?.('mine')}
                className="mobile-tabbar-btn text-slate-400"
              >
                <User className="mobile-tabbar-icon" />
                <span className="mobile-tabbar-label">我的</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="mobile-tabbar-inner">
            <button
              onClick={() => onNavigateTab?.('pan')}
              className="mobile-tabbar-btn text-amber-700"
            >
              <Compass className="mobile-tabbar-icon" />
              <span className="mobile-tabbar-label">排盘</span>
            </button>
            <button
              onClick={() => onNavigateTab?.('practice')}
              className="mobile-tabbar-btn text-slate-400"
            >
              <LayoutGrid className="mobile-tabbar-icon" />
              <span className="mobile-tabbar-label">应用</span>
            </button>
            <button
              onClick={() => onNavigateTab?.('study')}
              className="mobile-tabbar-btn text-slate-400"
            >
              <BookOpen className="mobile-tabbar-icon" />
              <span className="mobile-tabbar-label">学习</span>
            </button>
            <button
              onClick={() => onNavigateTab?.('mine')}
              className="mobile-tabbar-btn text-slate-400"
            >
              <User className="mobile-tabbar-icon" />
              <span className="mobile-tabbar-label">我的</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DalurenPanPage;
